import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Input,
  Label,
  SearchableCombobox,
} from "@nexus/ui";
import type { AdminVendorRow } from "@/features/admin/marketplace/types/admin-marketplace.types";
import type { VendorLabelGroup } from "./import-readiness";

type VendorMappingMode = "existing" | "new";

interface BulkVendorMappingRowProps {
  group: VendorLabelGroup;
  vendors: AdminVendorRow[];
  disabled: boolean;
  onMapExisting: (vendorId: string) => void;
  onMapNew: (contactEmail: string) => void;
}

export function BulkVendorMappingRow({
  group,
  vendors,
  disabled,
  onMapExisting,
  onMapNew,
}: BulkVendorMappingRowProps) {
  const { t } = useTranslation();
  const isMapped = Boolean(group.mappedVendorId || group.pendingVendorContactEmail);
  const initialMode: VendorMappingMode =
    group.pendingVendorContactEmail && !group.mappedVendorId ? "new" : "existing";
  const [mode, setMode] = useState<VendorMappingMode>(initialMode);
  const [contactEmail, setContactEmail] = useState(group.pendingVendorContactEmail ?? "");

  function handleModeChange(nextMode: VendorMappingMode) {
    setMode(nextMode);
    if (nextMode === "existing") {
      setContactEmail(group.pendingVendorContactEmail ?? "");
    }
  }

  return (
    <article className="rounded-lg border border-border/50 bg-muted/10 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold">{group.label}</h3>
            <Badge variant="secondary">
              {t("marketplace.admin.import.bulkVendorRowCount", { count: group.rowCount })}
            </Badge>
            {isMapped ? (
              <Badge variant="outline" className="border-primary/30 text-primary">
                {t("marketplace.admin.import.vendorMapped")}
              </Badge>
            ) : (
              <Badge variant="outline" className="border-status-warning/30 text-status-warning">
                {t("marketplace.admin.import.vendorUnmapped")}
              </Badge>
            )}
          </div>
          {group.mappedVendorName ? (
            <p className="text-xs text-muted-foreground">
              {t("marketplace.admin.import.vendorMappedTo", { name: group.mappedVendorName })}
            </p>
          ) : group.pendingVendorContactEmail ? (
            <p className="text-xs text-muted-foreground">
              {t("marketplace.admin.import.newVendorCaptured", {
                email: group.pendingVendorContactEmail,
              })}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 gap-1">
          <Button
            type="button"
            size="sm"
            variant={mode === "existing" ? "default" : "outline"}
            disabled={disabled}
            onClick={() => handleModeChange("existing")}
          >
            {t("marketplace.admin.import.vendorModeExisting")}
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === "new" ? "default" : "outline"}
            disabled={disabled}
            onClick={() => handleModeChange("new")}
          >
            {t("marketplace.admin.import.vendorModeNew")}
          </Button>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        <Label>{t("marketplace.admin.import.vendorMappingLabel")}</Label>
        {mode === "existing" ? (
          <SearchableCombobox
            items={vendors}
            getKey={(vendor) => vendor.id}
            getLabel={(vendor) => vendor.name}
            value={group.mappedVendorId}
            placeholder={t("marketplace.admin.import.vendorPlaceholder")}
            searchPlaceholder={t("marketplace.admin.import.vendorSearch")}
            emptyMessage={t("marketplace.admin.import.vendorEmpty")}
            onSelect={(vendor) => onMapExisting(vendor.id)}
          />
        ) : (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Label htmlFor={`new-vendor-email-${group.label}`} className="sr-only">
                {t("marketplace.admin.import.newVendorEmail")}
              </Label>
              <Input
                id={`new-vendor-email-${group.label}`}
                type="email"
                value={contactEmail}
                disabled={disabled}
                placeholder={t("marketplace.admin.import.newVendorEmailPlaceholder")}
                onChange={(event) => setContactEmail(event.target.value)}
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              disabled={disabled || !contactEmail.trim()}
              onClick={() => onMapNew(contactEmail.trim())}
            >
              {t("marketplace.admin.import.newVendorApply")}
            </Button>
          </div>
        )}
      </div>
    </article>
  );
}
